/** One thing that can be dropped between: a block, or a list item with the nested list under it. */
export interface DropUnit {
  /** The block ID a move would be placed in front of. */
  id: string;
  /** Whether it is part of what is being dragged. */
  moving: boolean;
}

export interface UnitBox {
  top: number;
  bottom: number;
}

export interface DropTarget {
  /** Index into the units the dragged blocks would be placed in front of; `units.length` is the end. */
  index: number;
  /** The ID to move in front of, or `null` for the end. */
  beforeId: string | null;
  /** Where to draw the insertion line, in the same coordinates `measure` returns. */
  lineY: number;
  /** Dropping here would change nothing: it is where the dragged blocks already are. */
  unchanged: boolean;
}

/**
 * Finds the gap nearest the pointer among vertically stacked siblings. `measure` is called for
 * O(log n) units per lookup (a binary search, then the neighbours of the hit), so a caller that
 * caches it never measures the whole document, however long it is.
 */
export function resolveDrop(
  units: readonly DropUnit[],
  measure: (index: number) => UnitBox | null,
  pointerY: number,
): DropTarget | null {
  if (units.length === 0) return null;
  // The first unit whose vertical middle is below the pointer: the drop goes in front of it.
  let low = 0;
  let high = units.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    const box = measure(middle);
    if (box === null) return null;
    if ((box.top + box.bottom) / 2 <= pointerY) low = middle + 1;
    else high = middle;
  }
  const index = low;
  const before = index < units.length ? measure(index) : null;
  const after = index > 0 ? measure(index - 1) : null;
  if (before === null && after === null) return null;
  const lineY =
    before !== null && after !== null
      ? (after.bottom + before.top) / 2
      : before !== null
        ? before.top
        : after!.bottom;

  // In front of, or right behind, the run being dragged is where it already is.
  let start = index;
  while (start > 0 && units[start - 1]!.moving) start--;
  let end = index;
  while (end < units.length && units[end]!.moving) end++;
  const movingCount = units.filter((unit) => unit.moving).length;
  const unchanged = end - start === movingCount && movingCount > 0;

  // Never name a dragged block as the one to move in front of.
  let target = index;
  while (target < units.length && units[target]!.moving) target++;
  return { index, beforeId: units[target]?.id ?? null, lineY, unchanged };
}

export interface ScrollEdge {
  top: number;
  bottom: number;
}

/** Pixels to scroll this frame: faster the deeper the pointer is into the edge zone, else zero. */
export function autoScrollSpeed(
  pointerY: number,
  visible: ScrollEdge,
  zone = 48,
  max = 18,
): number {
  if (pointerY < visible.top + zone) {
    return -Math.ceil(max * Math.min(1, (visible.top + zone - pointerY) / zone));
  }
  if (pointerY > visible.bottom - zone) {
    return Math.ceil(max * Math.min(1, (pointerY - (visible.bottom - zone)) / zone));
  }
  return 0;
}
